'use client';

import { useCallback, useContext, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useSearchParams } from 'next/navigation';
import { interactionResponse } from '@zillow/yield-callback';
import { logError } from '@zg-rentals/log-error';
import { useRouter } from '@/src/i18n/navigation';
import { area as areaApi, type AreaResponse, type AutocompleteResult } from '@/src/lib/api/endpoints/area';
import { useCurrentArea } from '@/src/lib/hooks/query/useCurrentArea';
import { useRecentSearch, useUpdateRecentSearch } from '@/src/lib/hooks/query/useRecentSearch';
import { useCurrentUser } from '@/src/lib/hooks/query/useUserQuery';
import { usePrepareMapForAreaNavigation, usePrepareMapForListingNavigation } from '@/src/lib/hooks/map';
import { getMoreFiltersCount } from '@/src/lib/filters/filterLabels';
import { FilterContext } from '@/src/lib/filters/FilterProvider';
import { DEFAULT_FILTERS, type FilterState } from '@/src/lib/filters/filterTypes';
import { toSearchScope } from '@/src/lib/filters/searchScope';
import { resolveListingAutocompleteHref } from '@/src/lib/utils/resolveListingAutocompleteHref';
import { buildRecentSearchBody, getAreaBounds } from '@/src/lib/savedSearches';
import { buildSearchUrl } from './buildSearchUrl';
import { emitSearchFilterChangeEvents, type FilterTrackingContext, SRP_FILTER_TRACKING } from './filterAnalytics';

export type ListingSearchMode = 'live' | 'navigate';

/**
 * Resolves the area's bounds (from cache when we already hold them) and writes
 * the recent search. Module scope so the deferred callback stays flat and
 * captures only these four values.
 */
function writeRecentSearch({
  knownArea,
  resourceId,
  filters,
  updateRecentSearch,
}: {
  knownArea: AreaResponse | null | undefined;
  resourceId: string;
  filters: FilterState;
  updateRecentSearch: (body: ReturnType<typeof buildRecentSearchBody>) => void;
}): void {
  void (knownArea ? Promise.resolve(knownArea) : areaApi.byResourceId(resourceId))
    .then((area) => {
      const bounds = getAreaBounds(area);
      if (!area?.id || !bounds) return;

      // `areas` is dropped for university areas (HPWEB-5750: unsearchable ids).
      const isUniversity = area.type === 'university';
      updateRecentSearch(
        buildRecentSearchBody({
          filters,
          searchSlug: 'apartments-for-rent',
          geo: {
            areaId: isUniversity ? null : area.id,
            lat: isUniversity ? undefined : area.coordinates?.lat,
            lon: isUniversity ? undefined : area.coordinates?.lon,
            bounds,
            searchScope: !isUniversity ? toSearchScope(area) : undefined,
          },
        }),
      );
    })
    .catch(() => {
      // Fire-and-forget: a failed area lookup must never break navigation.
    });
}

type UseListingSearchStateArgs = {
  mode: ListingSearchMode;
  trackingContext?: FilterTrackingContext | null;
  nearMeArea?: AreaResponse | null;
  seedFromRecentSearch?: boolean;
};

type UseListingSearchStateReturn = {
  filters: FilterState;
  setFilters: (next: FilterState) => void;
  applyFilters: (patch: Partial<FilterState>, options?: { immediate?: boolean }) => void;
  area: AreaResponse | AutocompleteResult | null;
  setArea: (result: AutocompleteResult) => void;
  submit: () => void;
  searchArea: (result: AutocompleteResult) => void;
  isPending: boolean;
  activeFilterCount: number;
  markUserInteraction: () => void;
};

/**
 * Encapsulates the live-vs-navigate state divergence so child components
 * don't have to know which mode they're in.
 *
 * - `live` (sm / SRP header): every filter mutation immediately calls
 *   `useFilters().setFilters(...)`, which writes URL via FilterProviderAppRouter.
 *   `submit()` is a no-op; the search icon is decorative confirmation.
 *
 * - `navigate` (lg / homepage hero): filters mutate a local draft. `submit()`
 *   builds the SRP URL via `buildSearchUrl` and pushes it inside a transition.
 */
export function useListingSearchState({
  mode,
  nearMeArea,
  seedFromRecentSearch,
  trackingContext = SRP_FILTER_TRACKING,
}: UseListingSearchStateArgs): UseListingSearchStateReturn {
  const router = useRouter();
  const searchParams = useSearchParams();
  // Read FilterContext optionally — `navigate` mode (homepage hero) doesn't
  // mount a FilterProvider, while `live` mode (SRP/HDP) always does. Live
  // mode enforces the provider below.
  const filterContext = useContext(FilterContext);
  if (mode === 'live' && !filterContext) {
    throw new Error('ListingSearch mode="live" requires a FilterProvider ancestor');
  }
  const { data: currentArea } = useCurrentArea();
  const { data: currentUser } = useCurrentUser();
  const { mutate: updateRecentSearch } = useUpdateRecentSearch();
  const prepareMapForAreaNavigation = usePrepareMapForAreaNavigation();
  const prepareMapForListingNavigation = usePrepareMapForListingNavigation();

  const [draftFilters, setDraftFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [draftArea, setDraftArea] = useState<AutocompleteResult | null>(null);
  const [isTransitionPending, startTransition] = useTransition();
  // True from the gesture until the transition takes over, so the Search button
  // reads busy across the after-paint gap too rather than staying clickable.
  const [hasPendingPush, setHasPendingPush] = useState(false);
  const isPending = isTransitionPending || hasPendingPush;
  // Discards a stale listing-detail resolution if the user picks another
  // result before the in-flight fetch for the previous one settles.
  const latestListingRequestRef = useRef<string | null>(null);
  // What `currentArea` was when a live-mode pick was staged, so the release
  // effect below can tell "hasn't settled yet" from "has moved on".
  const stagedAgainstAreaRef = useRef<string | null>(null);

  const isLoggedIn = currentUser?.loggedIn ?? false;
  const shouldSeedRecentSearch = (seedFromRecentSearch ?? false) && mode === 'navigate' && isLoggedIn;
  const { data: recentSearch } = useRecentSearch({ enabled: shouldSeedRecentSearch });

  // Seed the pristine draft with the recent search; any form interaction cancels seeding for good.
  const userTouchedRef = useRef(false);
  const markUserInteraction = useCallback(() => {
    userTouchedRef.current = true;
  }, []);
  useEffect(() => {
    if (!shouldSeedRecentSearch || !recentSearch || userTouchedRef.current) return;
    // The draft is untouched here, so absent fields reset it — a cleared or
    // locationless recent search must not leave older seeded criteria behind.
    setDraftFilters(recentSearch.filters ?? DEFAULT_FILTERS);
    setDraftArea(recentSearch.area ?? null);
  }, [shouldSeedRecentSearch, recentSearch]);

  // Logout must clear seeded values (they're the previous user's data) and re-arm seeding.
  const wasLoggedInRef = useRef(isLoggedIn);
  useEffect(() => {
    if (wasLoggedInRef.current && !isLoggedIn) {
      setDraftFilters(DEFAULT_FILTERS);
      setDraftArea(null);
      userTouchedRef.current = false;
    }
    wasLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  const committedFilters = mode === 'live' ? filterContext!.filters : draftFilters;

  // Deferred, coalesced filter applies (applyFilters): committing filters
  // re-renders every FilterContext consumer (SRP grid, map, pills) and writes
  // the URL — too heavy to run inside a click (216-312ms of INP on the
  // bed/bath toggles). Patches merge latest-wins and flush once after paint;
  // merging patches (not whole filter states) keeps a same-frame bed+bath
  // pair from clobbering each other's category. `stagedPatch` overlays the
  // staged-but-unflushed patch onto the returned `filters`, so every consumer
  // (triggers, badge, modal) sees what the user believes is selected.
  // `immediate` skips the deferral for callers that already deferred once
  // (PriceDropdown flushes after paint — chaining a second wait would break
  // its documented one-frame budget and briefly revert its controlled inputs).
  const pendingPatchRef = useRef<Partial<FilterState> | null>(null);
  const applyScheduledRef = useRef(false);
  const [stagedPatch, setStagedPatch] = useState<Partial<FilterState> | null>(null);
  // Drop (not flush) a patch staged at unmount: flushing would write the URL
  // in live mode while the user is already navigating away.
  useEffect(() => () => void (pendingPatchRef.current = null), []);

  // Committed filters plus any staged patch — what the user believes is
  // selected right now.
  const filters = stagedPatch ? { ...committedFilters, ...stagedPatch } : committedFilters;

  // Read-your-write mirrors, both modes: URL builds and flushes happen
  // synchronously in the same tick as a stage or commit, before React renders
  // the state update. (Same "navigate with the value in hand" reason
  // searchArea takes its result as an argument.) Reading through the refs also
  // keeps buildLiveAreaUrl/setArea/applyFilters referentially stable across
  // filter commits, which is what lets the memoized LocationCombobox skip
  // flush re-renders. `filtersRef` tracks the shown filters (committed +
  // staged); `committedFiltersRef` tracks committed only (the flush base).
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const committedFiltersRef = useRef(committedFilters);
  committedFiltersRef.current = committedFilters;
  const contextSetFiltersRef = useRef(filterContext?.setFilters);
  contextSetFiltersRef.current = filterContext?.setFilters;

  // Full-state write: supersedes any staged patch (callers like the
  // All-Filters modal build their draft from the shown filters).
  const setFilters = useCallback(
    (next: FilterState) => {
      userTouchedRef.current = true;
      pendingPatchRef.current = null;
      setStagedPatch(null);
      filtersRef.current = next;
      committedFiltersRef.current = next;
      if (mode === 'live') {
        contextSetFiltersRef.current?.(next);
      } else {
        setDraftFilters(next);
      }
    },
    [mode],
  );

  const flushPendingPatch = useCallback(() => {
    const pending = pendingPatchRef.current;
    if (!pending) return;
    const current = committedFiltersRef.current;
    const next = { ...current, ...pending };
    if (trackingContext) emitSearchFilterChangeEvents(current, next, trackingContext);
    setFilters(next);
  }, [setFilters, trackingContext]);

  const applyFilters = useCallback(
    (patch: Partial<FilterState>, { immediate = false }: { immediate?: boolean } = {}) => {
      // Mark synchronously, so the recent-search seeding effect can't
      // overwrite the draft between this interaction and the deferred flush.
      userTouchedRef.current = true;
      pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };
      filtersRef.current = { ...filtersRef.current, ...patch };
      if (immediate) {
        flushPendingPatch();
        return;
      }
      setStagedPatch(pendingPatchRef.current);
      if (applyScheduledRef.current) return;
      applyScheduledRef.current = true;
      void (async () => {
        await interactionResponse();
        applyScheduledRef.current = false;
        flushPendingPatch();
      })();
    },
    [flushPendingPatch],
  );

  const buildLiveAreaUrl = useCallback(
    (resourceId: string) => {
      const baseUrl = buildSearchUrl(resourceId, filtersRef.current);
      if (searchParams?.get('view') !== 'map') return baseUrl;

      const [pathname, queryString] = baseUrl.split('?');
      const params = new URLSearchParams(queryString);
      params.set('view', 'map');
      return `${pathname}?${params.toString()}`;
    },
    [searchParams],
  );

  // Bumped per scheduled navigation and on unmount, so a superseded selection
  // (or an unmounted bar) drops its pending push instead of navigating late.
  const pushGenerationRef = useRef(0);
  useEffect(() => () => void (pushGenerationRef.current += 1), []);

  /**
   * Navigates, after the browser has painted the interaction that asked for it.
   *
   * Starting the route change is the expensive half of a suggestion tap (~64ms
   * of its ~160ms INP at 8x CPU throttle — measured by comparing a tap that
   * changes area against one re-picking the current area), and the user isn't
   * waiting on it. `startTransition` alone doesn't defer it: React commits a
   * transition in the same frame when nothing interrupts.
   *
   * `beforeNavigate` carries the effects that only make sense if this
   * navigation actually happens — seeding map state, persisting the search — so
   * a superseded or unmounted push doesn't leave them applied for a page the
   * user never reached.
   */
  const pushUrl = useCallback(
    (url: string, beforeNavigate?: () => void) => {
      const generation = ++pushGenerationRef.current;
      setHasPendingPush(true);
      // `await`, never `.then()` — the monorepo's vitest config aliases
      // `interactionResponse` to a `vi.fn()` that returns undefined.
      void (async () => {
        try {
          await interactionResponse();
          if (generation !== pushGenerationRef.current) return;
          beforeNavigate?.();
          startTransition(() => {
            router.push(url);
          });
          // `useTransition`'s own isPending covers it from here.
          setHasPendingPush(false);
        } catch (error) {
          setHasPendingPush(false);
          logError({ error, errorType: 'ListingSearchNavigationError', context: { url } });
        }
      })();
    },
    [router],
  );

  // Persists the executed search (HP-8422); fixed slug because buildSearchUrl always targets apartments-for-rent.
  // Callers hand this to `pushUrl` as a `beforeNavigate` effect, which is both
  // what keeps its fetch off the interaction and what ties it to a navigation
  // that really happens.
  const persistRecentSearch = useCallback(
    (resourceId: string, filters: FilterState) => {
      if (!currentUser?.loggedIn) return;

      const knownArea = [currentArea, nearMeArea].find(
        (candidate) => candidate?.resourceId === resourceId && getAreaBounds(candidate),
      );

      writeRecentSearch({ knownArea, resourceId, filters, updateRecentSearch });
    },
    [currentUser, currentArea, nearMeArea, updateRecentSearch],
  );

  // Single dispatch point for navigate-mode area searches so Enter-search,
  // button-submit, and click-select can't drift apart.
  const pushSearchUrl = useCallback(
    (resourceId: string) => {
      const searchFilters = filtersRef.current;
      pushUrl(buildSearchUrl(resourceId, searchFilters), () => persistRecentSearch(resourceId, searchFilters));
    },
    [pushUrl, persistRecentSearch],
  );

  const setArea = useCallback(
    (result: AutocompleteResult) => {
      // Selecting an area builds the next URL from the current filters, which
      // must include any staged patch.
      flushPendingPatch();
      markUserInteraction();
      // Property autocomplete hits — navigate straight to the HDP regardless
      // of mode. The autocomplete API only returns a bare lotIdEncoded, not a
      // route, so the listing detail has to be fetched to get the canonical
      // /pad URL before we can push anywhere.
      if (result.lotIdEncoded) {
        const lotIdEncoded = result.lotIdEncoded;
        const suggestionResourceId = result.resourceId;
        latestListingRequestRef.current = lotIdEncoded;

        void resolveListingAutocompleteHref(lotIdEncoded).then((resolution) => {
          if (latestListingRequestRef.current !== lotIdEncoded) return undefined;

          if (resolution.type === 'href') {
            pushUrl(resolution.href, () => prepareMapForListingNavigation(lotIdEncoded));
            return undefined;
          }

          const resourceId = resolution.type === 'resourceId' ? resolution.resourceId : suggestionResourceId;
          if (!resourceId) return undefined;
          if (mode === 'live') {
            const searchFilters = filtersRef.current;
            pushUrl(buildLiveAreaUrl(resourceId), () => persistRecentSearch(resourceId, searchFilters));
          } else {
            pushSearchUrl(resourceId);
          }
          return undefined;
        });
        return;
      }
      if (mode === 'live') {
        if (result.resourceId) {
          const resourceId = result.resourceId;
          const searchFilters = filtersRef.current;
          // Stage the pick so the search bar's label changes in this tick.
          // `area` otherwise falls back to `currentArea`, which only settles
          // after the navigation's server round trip, leaving the field on the
          // previous area for most of a second. The effect below drops it once
          // `currentArea` moves.
          stagedAgainstAreaRef.current = currentArea?.resourceId ?? null;
          setDraftArea(result);
          pushUrl(buildLiveAreaUrl(resourceId), () => {
            if (resourceId !== currentArea?.resourceId) {
              prepareMapForAreaNavigation(resourceId);
            }
            persistRecentSearch(resourceId, searchFilters);
          });
        }
        return;
      }
      setDraftArea(result);
    },
    [
      buildLiveAreaUrl,
      currentArea?.resourceId,
      flushPendingPatch,
      markUserInteraction,
      mode,
      persistRecentSearch,
      prepareMapForAreaNavigation,
      prepareMapForListingNavigation,
      pushUrl,
      pushSearchUrl,
    ],
  );

  const targetResourceId = draftArea?.resourceId ?? currentArea?.resourceId ?? nearMeArea?.resourceId ?? null;

  const submit = useCallback(() => {
    // submit() builds the URL synchronously, so a Search click inside the
    // deferred-apply window must not read pre-patch filters (e.g. Reset
    // price, then Search).
    flushPendingPatch();
    markUserInteraction();
    if (mode !== 'navigate' || !targetResourceId) return;
    pushSearchUrl(targetResourceId);
  }, [flushPendingPatch, markUserInteraction, mode, targetResourceId, pushSearchUrl]);

  // Immediate search for a suggestion chosen via Enter in the location
  // combobox. Navigates with the result in hand instead of reading
  // `draftArea`, which hasn't flushed when selection and search share one
  // keystroke. Listings are excluded (`setArea` already navigates those in
  // every mode), as is live mode (selection itself navigates there — so on
  // the SRP an Enter-chosen suggestion still searches, via `setArea`).
  const searchArea = useCallback(
    (result: AutocompleteResult) => {
      flushPendingPatch();
      markUserInteraction();
      if (mode !== 'navigate' || result.lotIdEncoded || !result.resourceId) return;
      pushSearchUrl(result.resourceId);
    },
    [flushPendingPatch, markUserInteraction, mode, pushSearchUrl],
  );

  // Count modal-reachable filters only — Price, Beds, Baths sit on the
  // inline strip with their own active-state styling, so they shouldn't
  // double-count toward the All-filters badge.
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  const activeFilterCount = useMemo(() => getMoreFiltersCount(filters), [filters]);

  // Release the optimistic live-mode selection as soon as `currentArea` moves
  // at all, not just when it lands on the pick. The draft exists only to cover
  // the navigation round trip, and `area` resolves `draftArea ?? currentArea` —
  // so a draft that outlives its trip masks every later area change, whether
  // that's the map being panned somewhere else mid-flight or a navigation that
  // never lands on the pick at all.
  //
  // Navigate mode keeps its draft until Search runs; that's the point of it.
  useEffect(() => {
    if (mode !== 'live' || !draftArea) return;
    if ((currentArea?.resourceId ?? null) === stagedAgainstAreaRef.current) return;
    setDraftArea(null);
  }, [mode, draftArea, currentArea?.resourceId]);

  const area = useMemo<AreaResponse | AutocompleteResult | null>(
    () => draftArea ?? currentArea ?? nearMeArea ?? null,
    [draftArea, currentArea, nearMeArea],
  );

  return {
    filters,
    setFilters,
    applyFilters,
    area,
    setArea,
    submit,
    searchArea,
    isPending,
    activeFilterCount,
    markUserInteraction,
  };
}
